import { useEffect, useState } from "react";
import { Modal } from "antd";
import { CloseOutlined, RightOutlined, BulbOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  APP_VERSION,
  RELEASE_SEEN_STORAGE_KEY,
  RELEASE_FEATURES,
} from "../utils/constants";
import "./WhatsNewModal.css";

/**
 * Shows a "Latest Updates" modal once per app version when the user is authenticated
 * (first login after update or first refresh after update). Carousel-style design
 * with intro slide and features slide. Dismissing marks the version as seen in localStorage.
 */
export default function WhatsNewModal() {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [slide, setSlide] = useState(0);

  const totalSlides = 2; // 0: intro, 1: features
  const isLastSlide = slide === totalSlides - 1;

  useEffect(() => {
    if (typeof window === "undefined") return;
    const seenVersion = localStorage.getItem(RELEASE_SEEN_STORAGE_KEY);
    const shouldShow =
      seenVersion !== APP_VERSION && RELEASE_FEATURES?.length > 0;
    if (shouldShow) setOpen(true);
  }, []);

  const handleClose = () => {
    try {
      localStorage.setItem(RELEASE_SEEN_STORAGE_KEY, APP_VERSION);
    } catch (_) {}
    setOpen(false);
    setSlide(0);
  };

  const handleNext = () => {
    if (isLastSlide) handleClose();
    else setSlide((s) => s + 1);
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      closable={false}
      width={420}
      centered
      destroyOnClose
      className="whats-new-modal"
      styles={{ body: { padding: 0 } }}
    >
      <div className="whats-new-modal-header">
        <button
          type="button"
          className="whats-new-modal-close"
          onClick={handleClose}
          aria-label={t("cancel")}
        >
          <CloseOutlined />
        </button>
        <h2 className="whats-new-modal-title">{t("whatsNewLatestUpdates")}</h2>
        <button
          type="button"
          className="whats-new-modal-next"
          onClick={handleNext}
        >
          {isLastSlide ? t("whatsNewGotIt") : t("whatsNewNext")}
          {!isLastSlide && <RightOutlined />}
        </button>
      </div>

      <div className="whats-new-modal-body">
        {slide === 0 && (
          <>
            <div className="whats-new-modal-icon-wrap">
              <BulbOutlined />
            </div>
            <h3 className="whats-new-modal-heading">
              {t("whatsNewSeeWhatsChanged")}
            </h3>
            <p className="whats-new-modal-desc">
              {t("whatsNewIntro", { version: APP_VERSION })}
            </p>
          </>
        )}
        {slide === 1 && (
          <>
            <div className="whats-new-modal-icon-wrap">
              <BulbOutlined />
            </div>
            <h3 className="whats-new-modal-heading">
              {t("whatsNewTitle", { version: APP_VERSION })}
            </h3>
            <ul className="whats-new-modal-features">
              {RELEASE_FEATURES.map((feature, i) => (
                <li key={i}>{feature}</li>
              ))}
            </ul>
          </>
        )}
      </div>

      <div className="whats-new-modal-dots">
        {Array.from({ length: totalSlides }, (_, i) => (
          <span
            key={i}
            className={`whats-new-modal-dot ${i === slide ? "active" : ""}`}
            aria-hidden
          />
        ))}
      </div>
    </Modal>
  );
}
